import React from "react";
import { FaPlay, FaSearch, FaStethoscope, FaSyncAlt } from "react-icons/fa";
import { FaShield } from "react-icons/fa6";

const LineofEngagement = () => {
  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Left Side - Video & Contact */}
          <div className="lg:w-1/2">
            {/* Video Testimonial */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-8">
              <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-2xl">
                <iframe
                  className="w-full h-80 rounded-2xl"
                  src="https://www.youtube.com/embed/"
                  title="Client Testimonial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>

              {/* Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 cursor-pointer">
                  <FaPlay className="text-blue-600 text-lg ml-1" />
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Process */}
          <div className="lg:w-1/2">
            {/* Section Header */}
            <div className="mb-12">
              <h3 className="text-lg font-semibold text-gray-600 mb-2">
                How We Work
              </h3>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Our Approach
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                We firmly believe that correct diagnosis and understanding of
                individual needs is at the heart of any wellness service. We
                follow a 4-step ritual for all our services so our clients can
                experience maximum benefit from their treatments.
              </p>
            </div>

            {/* Process Steps */}
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex gap-6 group">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center group-hover:bg-blue-500 transition-all duration-300 relative">
                    <span className="text-2xl font-bold text-blue-600 group-hover:text-white transition-colors duration-300">
                      01
                    </span>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <FaSearch className="text-white text-xs" />
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                    Analyze
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    In-depth consultation to understand and analyze the root
                    cause and severity of your concerns. We conduct thorough
                    assessments to create your personalized wellness profile.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-6 group">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center group-hover:bg-green-500 transition-all duration-300 relative">
                    <span className="text-2xl font-bold text-green-600 group-hover:text-white transition-colors duration-300">
                      02
                    </span>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <FaShield className="text-white text-xs" />
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-300">
                    Prevent
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    Our experts recommend a holistic plan including homecare
                    routines and in-clinic services that help improve your
                    condition and prevent any future concerns from developing.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-6 group">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center group-hover:bg-purple-500 transition-all duration-300 relative">
                    <span className="text-2xl font-bold text-purple-600 group-hover:text-white transition-colors duration-300">
                      03
                    </span>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                      <FaStethoscope className="text-white text-xs" />
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors duration-300">
                    Treat
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    Result-oriented in-clinic services tailored to your specific
                    needs. We use advanced technologies and proven methods to
                    help you achieve your desired wellness goals effectively.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-6 group">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center group-hover:bg-orange-500 transition-all duration-300 relative">
                    <span className="text-2xl font-bold text-orange-600 group-hover:text-white transition-colors duration-300">
                      04
                    </span>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                      <FaSyncAlt className="text-white text-xs" />
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors duration-300">
                    Maintain
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    Sustain your results with regular maintenance services and
                    personalized homecare regimes. We provide ongoing support to
                    ensure long-term success and continued wellness.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LineofEngagement;
